import React from "react";

/**
 * The one place server-sanitised HTML is rendered.
 *
 * Its partner is `RichEditor` (TipTap), which is the one place that HTML is *written*.
 * Everything an investor or an admin authors goes editor → server allow-list → this
 * component, and nothing else in the app should call `dangerouslySetInnerHTML`.
 *
 * Why it exists: there were four copies of this line and they had already drifted.
 * `LegalPage` and `LegalGate` rendered the markup; `PostDetail` printed
 * `{post.body}` as *text*, so a reader saw literal `<p>` tags in a published post,
 * with `whitespace-pre-line` on top doubling every gap. One component means the next
 * surface that needs rich text cannot get it subtly wrong.
 *
 * Safety: the `html` passed here has already been through `sanitize_html` on the
 * server (see `routes_site.py`), which strips scripts, event handlers and
 * `javascript:` URLs against an allow-list. That is the guarantee this component
 * relies on — **never pass it a string that has not been sanitised server-side.**
 * Sanitising here instead would be a client-side check, which is not a check at all.
 */
/**
 * Authored HTML as plain text, for the places that cannot render markup at all:
 * a `<meta description>`, an Open Graph tag, a one-line teaser in a list.
 *
 * Those are not a licence to interpolate HTML as text on a page — that is the bug
 * this file exists to stop. They are the cases where the *destination* is a plain
 * string, and a bio that reads `<p><strong>test</strong></p>` in a Google result or a
 * shared link preview is the same bug wearing a different hat.
 */
export function toPlainText(html, limit = 0) {
  const text = String(html || "")
    .replace(/<br\s*\/?>|<\/(p|div|li|h[1-6])>/gi, " ")   // block ends become spaces
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ").replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"')
    .replace(/\s+/g, " ")
    .trim();
  if (limit && text.length > limit) return text.slice(0, limit).trimEnd() + "…";
  return text;
}

export default function RichText({ html, className = "", testid }) {
  if (!html) return null;
  return (
    <div
      data-testid={testid}
      // `legal-content` carries the typography for authored prose — headings, lists,
      // links, blockquotes. Named for where it started; it is the house style for
      // every rich-text surface, not just the legal pages.
      className={`legal-content ${className}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
